import { useEffect, useRef, useCallback } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

type Props = {
  name: string;
  onSignatureGenerated: (dataUrl: string | null) => void;
  disabled?: boolean;
};

const SIGNATURE_FONT = 'Lucida Calligraphy';
const SIGNATURE_FONT_SIZE = 64;
const CANVAS_HEIGHT = 150;

export const TypedSignature = ({
  name,
  onSignatureGenerated,
  disabled = false
}: Props) => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const generateSignature = useCallback(async () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const trimmedName = name.trim();

    if (!trimmedName) {
      onSignatureGenerated(null);
      return;
    }

    await document.fonts.ready;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const containerWidth = container.offsetWidth;

    canvas.width = containerWidth * ratio;
    canvas.height = CANVAS_HEIGHT * ratio;
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${CANVAS_HEIGHT}px`;

    ctx.scale(ratio, ratio);

    ctx.clearRect(0, 0, containerWidth, CANVAS_HEIGHT);

    const textColor = theme.palette.mode === 'dark' ? '#ffffff' : '#000000';
    ctx.fillStyle = textColor;
    ctx.font = `700 ${SIGNATURE_FONT_SIZE}px "${SIGNATURE_FONT}", cursive`;
    ctx.textBaseline = 'middle';

    const textWidth = ctx.measureText(trimmedName).width;
    const maxWidth = containerWidth - 40;
    const scale = textWidth > maxWidth ? maxWidth / textWidth : 1;

    ctx.save();
    const x = containerWidth / 2;
    const y = CANVAS_HEIGHT / 2;
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.textAlign = 'center';
    ctx.fillText(trimmedName, 0, 0);
    ctx.restore();

    const dataUrl = canvas.toDataURL('image/png');
    onSignatureGenerated(dataUrl);
  }, [name, theme.palette.mode, onSignatureGenerated]);

  useEffect(() => {
    generateSignature();
  }, [generateSignature]);

  useEffect(() => {
    const handleResize = () => {
      generateSignature();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [generateSignature]);

  const trimmedName = name.trim();

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        minHeight: CANVAS_HEIGHT,
        height: CANVAS_HEIGHT,
        border: `2px solid ${theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: disabled
          ? theme.palette.action.disabledBackground
          : theme.palette.background.paper,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {trimmedName ? (
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none'
          }}
        />
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic'
          }}
        >
          Type your name above to generate signature
        </Typography>
      )}
    </Box>
  );
};
